'use client';

import type { SharedEnergy } from '@slims/shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

interface EnergyFilters {
	from?: string;
	to?: string;
	podId?: string;
}

// Tabelle che alimentano la RPC energy_shared (riconciliazione gse > bolletta >
// energy_readings > estimated). Una modifica a una qualsiasi deve ricaricare i KPI.
const ENERGY_SOURCE_TABLES = [
	{ topic: 'energy-readings-realtime', table: 'energy_readings' },
	{ topic: 'estimated-data-realtime', table: 'estimated_data' },
	{ topic: 'gse-official-data-realtime', table: 'gse_official_data' },
	{ topic: 'energy-bill-readings-realtime', table: 'energy_bill_readings' },
] as const;

interface UseEnergyDataReturn {
	sharedEnergy: SharedEnergy[];
	isLoading: boolean;
	error: string | null;
	refetch: (filters?: EnergyFilters) => Promise<void>;
	totals: {
		totalImmessa: number;
		totalPrelevata: number;
		totalCondivisa: number;
	};
}

const EMPTY: SharedEnergy[] = [];

const energyKey = (orgId: string | null | undefined, f: EnergyFilters | undefined) =>
	['energy-shared', orgId, f?.from ?? null, f?.to ?? null, f?.podId ?? null] as const;

/**
 * ADR-006: backed by React Query. Il filtro iniziale è preso una volta al mount
 * (come prima: cambi successivi di `initialFilters` non ricaricano da sé) ed
 * entra nella query key, così due pannelli con lo stesso scope e filtro
 * condividono una sola richiesta. `refetch(f)` aggiorna il filtro e ricarica.
 */
export function useEnergyData(
	initialFilters: EnergyFilters | undefined,
	orgId: string | null | undefined,
): UseEnergyDataReturn {
	const queryClient = useQueryClient();
	const loaded = useAuthStore((s) => s.loaded);
	const enabled = loaded && !!orgId;

	const [filters, setFilters] = useState<EnergyFilters | undefined>(() => initialFilters);

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: energyKey(orgId, filters),
		enabled,
		queryFn: () => {
			const params: Record<string, string | number | boolean | undefined> = { orgId: orgId as string };
			if (filters?.from) params.from = filters.from;
			if (filters?.to) params.to = filters.to;
			if (filters?.podId) params.podId = filters.podId;
			return api.get<SharedEnergy[]>('/api/energy/shared', params);
		},
	});

	const refetch = useCallback(
		async (next?: EnergyFilters) => {
			if (next) setFilters(next);
			await rqRefetch();
		},
		[rqRefetch],
	);

	useEffect(() => {
		const unsubs = ENERGY_SOURCE_TABLES.map(({ topic, table }) =>
			subscribeToTable(topic, { event: '*', schema: 'public', table }, () => {
				queryClient.invalidateQueries({ queryKey: ['energy-shared', orgId] });
			}),
		);
		return () => {
			for (const unsub of unsubs) unsub();
		};
	}, [orgId, queryClient]);

	const sharedEnergy = Array.isArray(data) ? data : EMPTY;

	const totals = useMemo(
		() =>
			sharedEnergy.reduce(
				(acc, item) => ({
					totalImmessa: acc.totalImmessa + item.totalImmessa,
					totalPrelevata: acc.totalPrelevata + item.totalPrelevata,
					totalCondivisa: acc.totalCondivisa + item.energiaCondivisa,
				}),
				{ totalImmessa: 0, totalPrelevata: 0, totalCondivisa: 0 },
			),
		[sharedEnergy],
	);

	return {
		sharedEnergy,
		isLoading: enabled ? isLoading : false,
		error: error ? (error instanceof Error ? error.message : 'Errore nel caricamento dati energia') : null,
		refetch,
		totals,
	};
}
